/* eslint-disable radix */
/* eslint-disable arrow-body-style */
/* eslint-disable react/prop-types */
// @mui
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { Box, Chip, IconButton, Paper, Popover, Tooltip, Typography, TextField } from '@mui/material';
import '../../../app.css';
import dayjs from 'dayjs';
import { useState } from 'react';
import Iconify from '../../../components/iconify';

// ── Preview mode ─────────────────────────────────────────────────────────────
// Set to true to display mock data covering every color case for UI review.
// Flip back to false before merging.
const PREVIEW_MODE = false;

const buildMockStat = () => {
  // Anchor every entry to days 1–28 of the current month so they are always visible.
  const m = dayjs().format('YYYY-MM');
  const d = (n) => `${m}-${String(n).padStart(2, '0')}`;
  const today = dayjs().format('YYYY-MM-DD');
  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  return [
    // Full Day ≥ 6h
    { Date: d(30),  total_time: 5000 },  // 8h  → Full Day (progress full)
    { Date: d(2),  total_time: 28800 },  // 8h  → Full Day (progress full)
    { Date: d(3),  total_time: 25200 },  // 7h  → Full Day (partial progress)
    { Date: d(7),  total_time: 21600 },  // 6h  → Full Day (exactly 6h)
    // Half Day 4–6h
    { Date: d(4),  total_time: 18000 },  // 5h  → Half Day
    // Unplanned Leave — leave created after start date (createdAt > startDate)
    { Date: d(5),  total_time: 0, leave: { isHalfDay: false, halfDayType: null, isAutoLeave: false, startDate: d(5), createdAt: today } },
    // Planned Leave — created before or on start date (createdAt <= startDate)
    { Date: d(9),  total_time: 0,     leave: { isHalfDay: false, halfDayType: null,         isAutoLeave: false, startDate: d(9),  createdAt: yesterday } }, // Planned Full Day
    { Date: d(10), total_time: 18600, leave: { isHalfDay: true,  halfDayType: 'first_half', isAutoLeave: false, startDate: d(10), createdAt: yesterday } }, // Planned Half Day
    // Auto (system) leaves
    { Date: d(11), total_time: 0,     leave: { isHalfDay: false, halfDayType: null,          isAutoLeave: true, startDate: d(11), createdAt: d(11) } }, // Auto Full Day
    { Date: d(8),  total_time: 18000, leave: { isHalfDay: true,  halfDayType: 'second_half', isAutoLeave: true, startDate: d(8),  createdAt: d(8)  } }, // Auto Half Day
    // Public holiday
    { Date: d(16), total_time: 0, officeHoliday: true, officeHolidayName: 'Republic Day' },
    // Weekend — rendered automatically by day-of-week logic, no entry needed
  ];
};
// ─────────────────────────────────────────────────────────────────────────────

// ── Leave classification ──────────────────────────────────────────────────────
const isUnplannedLeave = (leaveItem) => {
  if (!leaveItem?.startDate || !leaveItem?.createdAt) return false;
  // "Unplanned" means the request was created after the leave's start date.
  const start = dayjs(leaveItem.startDate).startOf('day');
  const created = dayjs(leaveItem.createdAt);
  return created.isAfter(start);
};
// ─────────────────────────────────────────────────────────────────────────────

// ── Color tokens ────────────────────────────────────────────────────────────
const COLORS = {
  // Public holiday — info lighter (sky blue)
  holiday:         { bg: '#D0F2FF', border: '#74CAFF', text: '#04297A' },
  // Planned leave (both half & full) — warm coral
  plannedLeave:    { bg: '#FFE7D9', border: '#FFA48D', text: '#7A0C2E' },
  // Unplanned leave (< 4h worked, no manual leave) — muted red-orange
  unplannedLeave:  { bg: '#FFCCBC', border: '#FF7043', text: '#BF360C' },
  // Auto-generated (system) leaves — neutral / muted
  autoFull:        { bg: '#F4F6F8', border: '#C4CDD5', text: '#454F5B' },
  autoHalf:        { bg: '#EDE7F6', border: '#B39DDB', text: '#4A148C' },
  // Timer: Full Day ≥ 6h — light green
  timerFull:       { bg: '#E9FCD4', border: '#AAF27F', text: '#08660D' },
  // Timer: Half Day ≤ 4h — soft amber
  timerHalf:       { bg: '#FFF7CD', border: '#FFE16A', text: '#7A4F01' },
  // Timer running now — light blue
  inProgress:      { bg: '#D6E4FF', border: '#84A9FF', text: '#0C3483' },
  // Weekend
  weekend:         { bg: '#F4F6F8', border: '#DFE3E8', text: '#919EAB' },
};
// ────────────────────────────────────────────────────────────────────────────

export default function Calendar({ date, setDate, setMonth, monthlyStat, runningTask }) {
  const activeStat = PREVIEW_MODE ? buildMockStat() : monthlyStat;
  const [legendAnchor, setLegendAnchor] = useState(null);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper className="timelineCalendar" sx={{ overflow: 'hidden' }}>
        {PREVIEW_MODE && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label="PREVIEW MODE — mock data"
              size="small"
              sx={{ bgcolor: '#FFF7CD', border: '1px solid #FFE16A', color: '#7A4F01', fontWeight: 700, fontSize: 11 }}
            />
            <Typography variant="caption" sx={{ color: '#637381' }}>
              Set <code>PREVIEW_MODE = false</code> to restore live data.
            </Typography>
          </Box>
        )}
        <StaticDatePicker
          variant="static"
          orientation="portrait"
          value={date}
          disableFuture
          renderInput={(params) => <TextField {...params} />}
          componentsProps={{
            actionBar: { actions: [] },
          }}
          ToolbarComponent={(params) => <MyToolbar {...params} />}
          onChange={(newValue) => setDate(newValue)}
          onMonthChange={(month) => setMonth(month)}
          renderDay={(day, _value, DayComponentProps) => (
            <DayComponent
              key={dayjs(day).format('YYYY-MM-DD')}
              day={day}
              monthlyStat={activeStat}
              runningTask={runningTask}
              DayComponentProps={DayComponentProps}
            />
          )}
        />
        <Box sx={{ px: 2, pb: 1.5, pt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#637381', fontWeight: 600 }}>
            Color Guide
          </Typography>
          <Tooltip title="View color guide" placement="right">
            <IconButton
              size="small"
              onClick={(e) => setLegendAnchor(e.currentTarget)}
              sx={{ p: 0.25, color: '#637381', '&:hover': { color: '#2065D1' } }}
            >
              <Iconify icon="eva:info-outline" width={16} />
            </IconButton>
          </Tooltip>
        </Box>

        <Popover
          open={Boolean(legendAnchor)}
          anchorEl={legendAnchor}
          onClose={() => setLegendAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{ sx: { p: 2, maxWidth: 260, boxShadow: 3 } }}
        >
          <Typography variant="caption" sx={{ color: '#637381', fontWeight: 700, display: 'block', mb: 1.5 }}>
            Color Guide
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <LegendItem token={COLORS.inProgress}     label="In Progress"         hint="Timer is currently running" />
            <LegendItem token={COLORS.timerFull}      label="Full Day"            hint="Min. 6h/day · 40h/week" />
            <LegendItem token={COLORS.timerHalf}      label="Half Day"            hint="At least 4h but less than 6h" />
            <LegendItem token={COLORS.plannedLeave}   label="Planned Leave"       hint="Approved in advance" />
            <LegendItem token={COLORS.unplannedLeave} label="Unplanned Leave"     hint="Leave filed after the start date" />
            <LegendItem token={COLORS.autoFull}       label="Auto Full Day Leave"  hint="System generated" italic />
            <LegendItem token={COLORS.autoHalf}       label="Auto Half Day Leave"  hint="System generated" italic />
            <LegendItem token={COLORS.weekend}        label="Weekend" />
            <LegendItem token={COLORS.holiday}        label="Public Holiday" />
          </Box>
        </Popover>
      </Paper>
    </LocalizationProvider>
  );
}

const MyToolbar = (props) => (
  <Box sx={{ my: 1, mx: 3 }}>
    <Typography variant="overline" sx={{ color: '#637381' }}>
      Select Date
    </Typography>
    <Typography variant="h4">{dayjs(props?.parsedValue)?.format('MMM DD, YYYY')}</Typography>
  </Box>
);

const LegendItem = ({ token, label, hint, italic = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: token.bg,
        border: `1.5px solid ${token.border}`,
        flexShrink: 0,
        mt: '2px',
      }}
    />
    <Box>
      <Typography
        variant="caption"
        sx={{ color: '#161C24', fontStyle: italic ? 'italic' : 'normal', lineHeight: 1.3, display: 'block' }}
      >
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: '#637381', fontSize: 10, lineHeight: 1.2, display: 'block' }}>
          {hint}
        </Typography>
      )}
    </Box>
  </Box>
);

const DayComponent = ({ day, monthlyStat, runningTask, DayComponentProps }) => {
  const { key: dayKey, ...pickersDayProps } = DayComponentProps || {};

  if (DayComponentProps?.outsideCurrentMonth) {
    return <PickersDay key={dayKey} {...pickersDayProps} />;
  }

  const dateStr = dayjs(day).format('YYYY-MM-DD');
  const result = (monthlyStat || []).find((data) => {
    const rawDate = data?.Date;
    if (!rawDate) return false;
    return dayjs(rawDate).format('YYYY-MM-DD') === dateStr;
  });

  const isRunning =
    runningTask &&
    new Date(runningTask?.TimeTracker?.[0]?.startTime).toDateString() === new Date(day).toDateString();

  const leave = result?.leave || null;
  const isWeekend = [0, 6].includes(new Date(day).getDay());
  const isOfficeHoliday = Boolean(result?.officeHoliday);
  const officeHolidayName = result?.officeHolidayName || 'Office Holiday';

  const hasTimerData = Boolean(parseInt(result?.total_time) || isRunning);
  const hasLeave = Boolean(leave);

  if (!hasTimerData && !isWeekend && !isOfficeHoliday && !hasLeave) {
    return (
      <PickersDay
        key={dayKey}
        {...pickersDayProps}
        sx={{
          '&.Mui-selected': {
            bgcolor: 'transparent !important',
            color: 'inherit',
            fontWeight: 700,
            boxShadow: '0 0 0 2px #2065D1',
          },
          '&.Mui-selected:hover': {
            bgcolor: 'transparent !important',
          },
          '&.Mui-focusVisible': {
            boxShadow: '0 0 0 2px #2065D1',
            bgcolor: 'transparent',
          },
          '&:focus.Mui-selected': {
            bgcolor: 'transparent !important',
          },
        }}
      />
    );
  }

  const totalTime = parseInt(result?.total_time) || 0;
  const totalHour = Math.floor(totalTime / 3600);
  const totalMinute = Math.floor((totalTime % 3600) / 60);
  const workedHours = totalTime / 3600;
  const workPercentage = Math.min(100, Math.max(0, (totalTime / (8 * 3600)) * 100));

  // ── Resolve color token ──────────────────────────────────────────────────
  let token = COLORS.timerFull;
  let tooltipTitle = `${totalHour}h ${totalMinute}m`;

  if (isOfficeHoliday) {
    token = COLORS.holiday;
    tooltipTitle = officeHolidayName;
  } else if (hasLeave) {
    const { isHalfDay, isAutoLeave } = leave;
    const workedStr = totalTime > 0 ? ` — ${totalHour}h ${totalMinute}m` : '';
    if (isAutoLeave) {
      token = isHalfDay ? COLORS.autoHalf : COLORS.autoFull;
      tooltipTitle = isHalfDay ? `Auto Half Day Leave${workedStr}` : `Auto Full Day Leave${workedStr}`;
    } else if (isUnplannedLeave(leave)) {
      token = COLORS.unplannedLeave;
      tooltipTitle = isHalfDay ? `Unplanned Leave (Half Day)${workedStr}` : `Unplanned Leave (Full Day)${workedStr}`;
    } else {
      token = COLORS.plannedLeave;
      tooltipTitle = isHalfDay ? `Planned Leave (Half Day)${workedStr}` : `Planned Leave (Full Day)${workedStr}`;
    }
  } else if (isWeekend && !hasTimerData) {
    token = COLORS.weekend;
    tooltipTitle = 'Weekend';
  } else if (hasTimerData) {
    if (isRunning && workedHours < 4) {
      token = COLORS.inProgress;
      tooltipTitle = `In Progress — ${totalHour}h ${totalMinute}m`;
    } else if (workedHours < 6) {
      token = COLORS.timerHalf;
      tooltipTitle = `Half Day — ${totalHour}h ${totalMinute}m`;
    } else {
      token = COLORS.timerFull;
      tooltipTitle = `Full Day — ${totalHour}h ${totalMinute}m`;
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  const showProgressOverlay =
    hasTimerData && !hasLeave && !isWeekend && !isOfficeHoliday && workedHours < 8;

  // Progress ring: conic-gradient starts from top (-90deg), fills clockwise.
  // Sits exactly over the parent circle (inset:0, same 36×36px).
  // The mask punches out the centre, leaving a ~4px ring.
  const ringStyle = showProgressOverlay ? {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: `conic-gradient(
      from -90deg,
      ${token.border} 0% ${workPercentage}%,
      #fff ${workPercentage}% 100%
    )`,
    WebkitMask: 'radial-gradient(circle, transparent 62%, black 63%)',
    mask: 'radial-gradient(circle, transparent 62%, black 63%)',
    zIndex: 1,
    pointerEvents: 'none',
  } : null;

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2px',
        }}
      >
        <div
          style={{
            background: token.bg,
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: `1.5px solid ${token.border}`,
            position: 'relative',
            margin: '2px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 5,
          }}
        >
          {ringStyle && <div style={ringStyle} />}
          <PickersDay
            key={dayKey}
            {...pickersDayProps}
            sx={{
              zIndex: 2,
              opacity: 1,
              bgcolor: 'transparent',
              position: 'absolute',
              color: token.text,
              fontWeight: pickersDayProps.selected ? 700 : 600,
              '&.Mui-selected': {
                bgcolor: 'transparent',
                color: token.text,
              },
              '&.Mui-selected:hover': {
                bgcolor: 'transparent',
                color: token.text,
              },
              '&.Mui-focusVisible': {
                bgcolor: 'transparent',
                boxShadow: 'none',
                outline: 'none',
              },
              '&:focus.Mui-selected': {
                bgcolor: 'transparent',
                color: token.text,
              },
            }}
          />
        </div>
        {/* Selection dot — sits below the circle, invisible when not selected */}
        <div
          style={{
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            background: pickersDayProps.selected ? token.border : 'transparent',
            flexShrink: 0,
          }}
        />
      </div>
    </Tooltip>
  );
};
